import { WebRTCBase } from "./base";
import { CandidateType, OfferStatus, WebRTC } from "./webrtc";
import { ActionMessage, WebRTCChannel } from "./channel";
import { AudioDirection, CameraSpeakerAction, EventWebrtcAction, MediaKind, PresetWebrtcAction, SpeakerWebrtcAction, STREAM_TYPE_LIVE, STREAM_TYPE_PLAYBACK, STREAM_TYPE_TWO_WAY_AUDIO } from "../common/base";
import { AppError } from "../common/app_error";
import { AudioWebRTC, MutedKind } from "./audio_webrtc";
import { AudioDevice } from "./audio_device";
import { DataWebRTC, ExtraDataWebrtcLabel, ImgTag } from "./data_webrtc";
import { arrayBufferLikeCopy, removeTransceiverByMid } from "../common/util";
import { SequenceWaiter } from "../common/sequence_waiter";
import { DownloaderWebrtcAction } from "../common/downloader";
import { StreamWebRTC } from "./stream_webrtc";
import { EventQueue } from "../common/event_queue";
export var SinglePCAction;
(function (SinglePCAction) {
    SinglePCAction["StreamAdd"] = "stream_add";
    SinglePCAction["StreamRemove"] = "stream_remove";
})(SinglePCAction || (SinglePCAction = {}));
export class GeneralWebRTC extends WebRTC {
    constructor(params, settings) {
        params.peerConfig = params.peerConfig || {};
        params.peerConfig['encodedInsertableStreams'] = true;
        super(params, settings);
        // connection
        this.candidateType = CandidateType.None;
        this.streamMapMediaInfo = new Map();
        this.eventQueue = new EventQueue();
        //two way audio start
        this.audioDevice = null;
        this.audioSenders = new Set();
        this.senderCreatedStreams = new WeakSet();
        this.receiverCreatedStreams = new WeakMap();
        this.transferredCanvases = new WeakMap();
        //two way audio end
        //data webrtc temporarily
        this.dataRtcStarted = false;
        this.waiter = new SequenceWaiter("DataWebRTC");
        this.extralChannel = new Map();
        this.unconnectedLabels = Object.values(ExtraDataWebrtcLabel);
        this.addEventListener(WebRTC.EVENT_ACTION, (channel, msg) => {
            if (msg.act == AudioWebRTC.EVENT_SPEAKABLE) {
                if (msg.data) {
                    if (msg.data.speakable) {
                        this.bindAudioDevice(msg.data.speaker_connect_id);
                    }
                    else {
                        this.releaseDevice(msg.data.speaker_connect_id, MutedKind.Kicked);
                    }
                }
            }
        });
    }
    doClose() {
        super.doClose();
        this.clearTimeUpdate();
        this.audioSenders?.clear?.();
        if (this.audioDevice) {
            this.audioDevice?.close?.();
            this.audioDevice = null;
        }
    }
    initVideoTrack(receiver, mediaInfo, streamId) {
        console.debug('canvas id:', mediaInfo.canvas.id);
        let streams;
        if (this.receiverCreatedStreams.has(receiver)) {
            console.info('receiver created already:', receiver);
            streams = this.receiverCreatedStreams.get(receiver);
        }
        else {
            streams = receiver.createEncodedStreams();
            this.receiverCreatedStreams.set(receiver, streams);
        }
        // canvas
        let screen = null;
        if (this.transferredCanvases.has(mediaInfo.canvas)) {
            screen = this.transferredCanvases.get(mediaInfo.canvas);
        }
        else {
            mediaInfo.canvas.width = mediaInfo.video.width;
            mediaInfo.canvas.height = mediaInfo.video.height;
            mediaInfo.canvas.style.aspectRatio = '' + mediaInfo.canvas.width / mediaInfo.canvas.height;
            screen = mediaInfo.canvas.transferControlToOffscreen();
            this.transferredCanvases.set(mediaInfo.canvas, screen);
        }
        // config
        const config = {
            codec: '',
            description: ''
        };
        // worker
        let worker = this.getWorker(streamId);
        const trackReadable = streams.readable;
        worker.postMessage({
            type: WebRTCBase.ACTION_DECODE_VIDEO,
            args: { id: streamId, canvas: screen, trackReadable, config, debugging: this.workerDebug, fps: mediaInfo.video.fps, frameStats: this.featureSettings?.frameStats }
        }, [screen, trackReadable]);
    }
    reconnect(func) {
        this.eventQueue.enqueue((cb) => {
            super.reconnect(cb);
        });
    }
    setTimeUpdate() {
        this.clearTimeUpdate();
        this.timerId = setInterval(() => {
            this.workers.forEach((worker, key) => {
                worker.postMessage({ type: WebRTCBase.ACTION_GET_TIME, args: { sourceId: key } });
            });
        }, 200);
    }
    clearTimeUpdate() {
        if (typeof this.timerId === 'number') {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    }
    emitTimeUpdate(currentTime, sourceId) {
        if (typeof currentTime === 'number') {
            this.emitEvent(GeneralWebRTC.EVENT_TIME_UPDATE, currentTime, sourceId);
        }
    }
    onMessage(data) {
        if (super.onMessage(data))
            return true;
        switch (data.type) {
            case WebRTCBase.ACTION_GET_TIME_REPLY:
                this.emitTimeUpdate(data.args.currentTime, data.args.sourceId);
                return true;
            case WebRTCBase.ACTION_DECODE_IMAGE_REPLY:
                this.onDecodeImage(data.args);
                return true;
        }
        return false;
    }
    filterVideoCodec(transceiver) {
        if (transceiver.receiver &&
            transceiver.receiver.track &&
            transceiver.receiver.track.kind === "video") {
            // Get the supported codecs
            const supportedCodecs = RTCRtpReceiver.getCapabilities("video").codecs;
            const selectedCodecs = supportedCodecs.filter((codec) => codec.mimeType.toUpperCase().includes(this.selectedVideoCodec));
            if (selectedCodecs.length === 0) {
                throw new Error(`${this.selectedVideoCodec} codec is not supported on this browser`);
            }
            // take the first one
            transceiver.setCodecPreferences([selectedCodecs[0]]);
        }
        else {
            throw new Error("transceiver.receiver.track.kind is not video");
        }
    }
    filterAudioCodec(transceiver) {
        if (transceiver.receiver &&
            transceiver.receiver.track &&
            transceiver.receiver.track.kind === "audio") {
            // Get the supported audio codecs
            const supportedAudioCodecs = RTCRtpReceiver.getCapabilities("audio").codecs;
            // Filter for PCMA and PCMU, prioritize PCMA first
            const pcmaCodec = supportedAudioCodecs.find((codec) => codec.mimeType === GeneralWebRTC.FILTER_AUDIO_CODEC_A);
            const pcmuCodec = supportedAudioCodecs.find((codec) => codec.mimeType === GeneralWebRTC.FILTER_AUDIO_CODEC_U);
            const audioPreferences = [];
            if (pcmaCodec) {
                audioPreferences.push(pcmaCodec);
            }
            if (pcmuCodec) {
                audioPreferences.push(pcmuCodec);
            }
            if (audioPreferences.length > 0) {
                // Set codec preferences for audio, prioritize PCMA then PCMU
                transceiver.setCodecPreferences(audioPreferences);
            }
            else {
                throw new Error("PCMA or PCMU codec is not supported on this browser");
            }
        }
        else {
            throw new Error("transceiver.receiver.track.kind is not audio");
        }
    }
    getEventQueue() {
        return this.eventQueue;
    }
    startBatch(mediaSetting) {
        if (this.started) {
            console.warn('started already: connId=' + this.connId);
            return false;
        }
        this.tracerProvider.startAllSpan();
        const messageChannel = this.peerConn.createDataChannel(WebRTC.CHAN_ACTION_NAME);
        messageChannel.onerror = e => {
            this.emitClose();
            console.error('error in channel: connId=%s, error=%s', this.connId, e);
        };
        this.messageChannel = new WebRTCChannel({
            connId: this.connId,
            channel: messageChannel,
            onObject: this.onDataChannelAction.bind(this),
            onClose: this.onDataChannelClose.bind(this),
            onOpen: (channel) => {
                this.emitEvent(WebRTC.EVENT_MESSAGE_OPEN, channel);
            }
        });
        this.startExternalData();
        mediaSetting.forEach((setting) => {
            this.addStream(setting);
        });
        this.started = true;
        this.stats.start({ key: WebRTC.STATS_CONNECTION });
        // stop error popup
        this.createOffer(OfferStatus.Start).catch();
        return true;
    }
    addStream(streamInfo) {
        let mediaInfo = {
            audio: streamInfo.audio, video: streamInfo.video,
            canvas: streamInfo.canvas,
            audioMid: "",
            videoMid: ""
        };
        if (this.streamMapMediaInfo.has(streamInfo.streamId)) {
            console.error('Duplicate streamId: %s', streamInfo.streamId);
            this.emitEvent(WebRTC.EVENT_ERROR, new AppError({ name: SinglePCAction.StreamAdd, message: `${streamInfo.streamId} is already added`, code: -1, external: [streamInfo.streamId] }));
            return new AppError({ name: "duplicate", message: "Duplicate StreamId" });
        }
        if (streamInfo.audioDirection != null && streamInfo.audioDirection !== undefined && streamInfo.audioDirection !== AudioDirection.inactive) {
            let audioDir = AudioDirection[streamInfo.audioDirection];
            let audioTransceiver = this.peerConn.addTransceiver('audio', { direction: audioDir });
            mediaInfo.audioMid = audioTransceiver;
            this.filterAudioCodec(audioTransceiver);
        }
        if (streamInfo.video) {
            let videoTransceiver = this.peerConn.addTransceiver('video', { direction: 'recvonly' });
            mediaInfo.videoMid = videoTransceiver;
            this.filterVideoCodec(videoTransceiver);
        }
        this.streamMapMediaInfo.set(streamInfo.streamId, mediaInfo);
    }
    addStreamsAction(sInfos, offer, func, failedStreamInfos, successfulStreamInfos) {
        let streamTypes = [];
        sInfos.forEach((value) => {
            if (value.streamType === STREAM_TYPE_LIVE) {
                streamTypes.push({ streamParam: value.parameter, sourceId: value.sourceId });
            }
            else if (value.streamType === STREAM_TYPE_PLAYBACK) {
                streamTypes.push({ playbackParam: value.parameter, sourceId: value.sourceId });
            }
            else if (value.streamType === STREAM_TYPE_TWO_WAY_AUDIO) {
                streamTypes.push({ twoWayAudio: value.parameter, sourceId: value.sourceId });
            }
            failedStreamInfos.push(value);
        });
        let offerObj = { parameter: streamTypes, sdp: offer };
        const cb = async (err, result) => {
            if (err) {
                console.error('%s addStreamsAction error:%s', this.connId, err.toString());
                failedStreamInfos.forEach((value) => value.reason = err);
            }
            else {
                console.info('addStreamsAction result:', result);
                if (result.data?.streamResults) {
                    let failedSourceMap = new Map();
                    result.data.streamResults.forEach((item) => {
                        if (item.error) {
                            failedSourceMap.set(item.sourceId, item.error);
                        }
                        else {
                            if (this.streamMapMediaInfo.has(item.sourceId)) {
                                let mediaInfo = this.streamMapMediaInfo.get(item.sourceId);
                                if (item.streamInfo && item.streamInfo.VideoEncodeInfo && item.streamInfo.VideoEncodeInfo.Resolution) {
                                    mediaInfo.video = { width: item.streamInfo.VideoEncodeInfo.Resolution.Width, height: item.streamInfo.VideoEncodeInfo.Resolution.Height, fps: item.streamInfo.VideoEncodeInfo.FrameRate };
                                }
                                const successItem = failedStreamInfos.find((item) => item.sourceId === item.sourceId);
                                successItem.success = true;
                                successfulStreamInfos.push(successItem);
                            }
                            else {
                                console.warn(`mediaInfo is null for streamId: ${item.sourceId}`);
                            }
                        }
                    });
                    if (failedSourceMap.size > 0) {
                        failedStreamInfos.forEach((value) => {
                            if (failedSourceMap.has(value.sourceId)) {
                                try {
                                    value.reason = JSON.stringify(failedSourceMap.get(value.sourceId.toString()));
                                }
                                catch (e) {
                                    value.reason = failedSourceMap.get(value.sourceId.toString()).toString();
                                }
                            }
                        });
                    }
                }
                else {
                    failedStreamInfos.forEach((value) => value.reason = 'Invalid response');
                }
                if (result.data?.answer) {
                    await this.putAnswer(result.data.answer);
                }
                else {
                    failedStreamInfos.forEach((value) => value.reason = 'answer not found');
                    console.error('%s addStreamsAction error: answer not found', this.connId);
                }
            }
            failedStreamInfos = failedStreamInfos.filter((failedItem) => !successfulStreamInfos.find(successItem => successItem.sourceId === failedItem.sourceId));
            func(failedStreamInfos, successfulStreamInfos);
        };
        try {
            return this.sendActionMessage(GeneralWebRTC.ACTION_ADD_STREAM, offerObj, cb);
        }
        catch (e) {
            console.error(e.message, e.stack);
            func(failedStreamInfos, successfulStreamInfos);
        }
    }
    setTrack() {
        this.peerConn.ontrack = (event) => {
            event.streams.forEach((stream) => {
                const transceiver = event.transceiver;
                console.debug('Remote Stream ID:%s mid:%s', stream.id, transceiver.mid);
                let mediaInfo = this.streamMapMediaInfo.get(stream.id);
                if (mediaInfo) {
                    mediaInfo = this.fixStreamMapMid(mediaInfo, stream.id, transceiver.mid, event.track.kind);
                    this.streamMapMediaInfo.set(stream.id, mediaInfo);
                    if (event.track.kind == GeneralWebRTC.TRACK_KIND_VIDEO) {
                        this.initVideoTrack(event.receiver, mediaInfo, stream.id);
                    }
                    else if (event.track.kind == GeneralWebRTC.TRACK_KIND_AUDIO) {
                        if (this.receiverCreatedStreams.has(event.receiver)) {
                            console.info('%s receiver:%s created already', stream.id, transceiver.mid);
                        }
                        else {
                            const streams = event.receiver.createEncodedStreams();
                            streams.readable.pipeTo(streams.writable);
                            this.receiverCreatedStreams.set(event.receiver, streams);
                        }
                        mediaInfo.audio.srcObject = event.streams[0];
                        mediaInfo.audio.autoplay = true;
                    }
                }
                else {
                    console.warn('Stream setting not found:', stream.id);
                    this.emitEvent(WebRTC.EVENT_ERROR, new AppError({ name: SinglePCAction.StreamAdd, message: `${stream.id} mid not found`, code: -1, external: [stream.id] }));
                }
            });
        };
    }
    fixStreamMapMid(midInfo, streamId, mid, kind) {
        if (!midInfo) {
            console.warn('Stream %s mid not found:', streamId);
            return null;
        }
        const updateMid = (type, mid) => {
            const midValue = midInfo[type];
            const isTransceiver = midValue instanceof RTCRtpTransceiver;
            const curMid = isTransceiver ? midValue.mid : midValue;
            if (curMid !== mid) {
                console.warn('mid %s mid not match:%s in stream:%s', curMid, mid, streamId);
                for (let [, value] of this.streamMapMediaInfo) {
                    const valueType = value[type];
                    if (valueType instanceof RTCRtpTransceiver) {
                        if (valueType.mid === mid) {
                            value[type] = curMid;
                            break;
                        }
                    }
                    else if (valueType === mid) {
                        value[type] = curMid;
                        break;
                    }
                }
            }
            midInfo[type] = mid;
        };
        if (kind === GeneralWebRTC.TRACK_KIND_VIDEO) {
            updateMid('videoMid', mid);
        }
        else if (kind === GeneralWebRTC.TRACK_KIND_AUDIO) {
            updateMid('audioMid', mid);
        }
        return midInfo;
    }
    prepareRemoveMids() {
        const midArray = this.peerConn.getTransceivers()
            .map(transceiver => transceiver.mid)
            .filter((mid) => mid !== null);
        let localMids = new Set();
        this.streamMapMediaInfo.forEach(midInfo => {
            if (midInfo.audioMid) {
                if (typeof midInfo.audioMid === 'string') {
                    localMids.add(midInfo.audioMid);
                }
                else if (midInfo.audioMid instanceof RTCRtpTransceiver && midInfo.audioMid.mid) {
                    midInfo.audioMid = midInfo.audioMid.mid;
                    localMids.add(midInfo.audioMid);
                }
            }
            if (midInfo.videoMid) {
                if (typeof midInfo.videoMid === 'string') {
                    localMids.add(midInfo.videoMid);
                }
                else if (midInfo.videoMid instanceof RTCRtpTransceiver && midInfo.videoMid.mid) {
                    midInfo.videoMid = midInfo.videoMid.mid;
                    localMids.add(midInfo.videoMid);
                }
            }
        });
        let removeMids = [];
        midArray.forEach(mid => {
            if (!localMids.has(mid)) {
                removeMids.push(mid);
            }
        });
        return removeMids;
    }
    getMidsByStreamId(streamId) {
        if (!this.streamMapMediaInfo.has(streamId)) {
            console.warn('Stream %s mid not found:', streamId);
            this.emitEvent(WebRTC.EVENT_ERROR, new AppError({ name: SinglePCAction.StreamRemove, message: `${streamId} mid not found`, code: -1, external: [streamId] }));
            return [];
        }
        let midInfo = this.streamMapMediaInfo.get(streamId);
        let mids = [];
        if (midInfo.audioMid) {
            if (typeof midInfo.audioMid == 'string') {
                mids.push(midInfo.audioMid);
            }
            else if (midInfo.audioMid instanceof RTCRtpTransceiver && midInfo.audioMid.mid) {
                midInfo.audioMid = midInfo.audioMid.mid;
                mids.push(midInfo.audioMid);
            }
        }
        if (midInfo.videoMid) {
            if (typeof midInfo.videoMid == 'string') {
                mids.push(midInfo.videoMid);
            }
            else if (midInfo.videoMid instanceof RTCRtpTransceiver && midInfo.videoMid.mid) {
                midInfo.videoMid = midInfo.videoMid.mid;
                mids.push(midInfo.videoMid);
            }
        }
        return mids;
    }
    removeStreams(sourceIds, callback) {
        // remove stream in advance
        for (let id of sourceIds) {
            this.removeSourceId(id);
        }
        const mids = this.prepareRemoveMids();
        if (mids.length === 0) {
            console.warn('Stream mid not found:', sourceIds);
            callback(null, null);
            return;
        }
        // const transceivers = this.peerConn.getTransceivers()
        // for (let transceiver of transceivers) {
        //     if (transceiver.mid && mids.includes(transceiver.mid)) {
        //         this.peerConn.removeTrack(transceiver.sender)
        //         transceiver.direction = 'recvonly'//FIXME: workaround for Pion
        //     }
        // } 
        this.createOffer(OfferStatus.Remove).then((sdp) => {
            sdp = removeTransceiverByMid(sdp, mids);
            console.log('Stream removed:%s mid:%o, sdp:%s', sourceIds, mids, sdp);
            let cbInner = (err, result) => {
                if (err) {
                    console.error('removeStream error:', err);
                }
                else {
                    this.peerConn.setLocalDescription({ type: 'offer', sdp: sdp }).catch(e => {
                        this.emitError(e);
                    });
                    this.putAnswer(result.data).catch(e => {
                        this.emitError(e);
                    });
                }
                callback(err, result);
            };
            return this.removeStreamInner(sourceIds, sdp, cbInner);
        }).catch((e) => {
            console.error('removeStream error:', e);
            callback(AppError.fromError(e), null);
            this.emitEvent(WebRTC.EVENT_ERROR, new AppError({ name: SinglePCAction.StreamRemove, message: 'removeStream error', code: -1, external: sourceIds }));
            return;
        });
    }
    removeSourceId(sourceId) {
        console.log("removeSourceId", sourceId);
        this.streamMapMediaInfo.delete(sourceId);
        this.releaseDevice(sourceId, MutedKind.Close);
        if (this.workers.has(sourceId)) {
            this.workers.get(sourceId).terminate();
            this.workers.delete(sourceId);
        }
    }
    removeStreamInner(sourceIds, sdp, callback) {
        return this.sendActionMessage(GeneralWebRTC.ACTION_REMOVE_STREAM, { sdp, sourceIds: sourceIds }, callback);
    }
    async onAnswer(streamResults, streamInfos, answer) {
        let successfulSourceId = [];
        if (streamResults) {
            streamResults.forEach((item) => {
                if (item.error) {
                    console.warn(`streamResult error: ${item.error}, sourceId:`, item.sourceId);
                }
                else {
                    const streamInfo = streamInfos.find((it) => it.sourceId === item.sourceId);
                    if (streamInfo) {
                        let videoInfo = null;
                        if (item.streamInfo && item.streamInfo.VideoEncodeInfo && item.streamInfo.VideoEncodeInfo.Resolution) {
                            videoInfo = { width: item.streamInfo.VideoEncodeInfo.Resolution.Width, height: item.streamInfo.VideoEncodeInfo.Resolution.Height, fps: item.streamInfo.VideoEncodeInfo.FrameRate };
                            let mediaInfo = this.streamMapMediaInfo.get(item.sourceId);
                            if (mediaInfo) {
                                mediaInfo.video = videoInfo;
                            }
                        }
                        successfulSourceId.push(item.sourceId);
                    }
                    else {
                        console.warn(`streamInfo is null for sourceId: ${item.sourceId}`);
                    }
                }
            });
        }
        this.setTrack();
        this.setTimeUpdate();
        await this.putAnswer(answer);
        let failedSourceId = streamInfos.map((value) => value.sourceId).filter((value) => !successfulSourceId.includes(value));
        return failedSourceId;
    }
    // two way audio start
    speakControl(params, action, callback) {
        const that = this;
        console.log("SpeakControl", "macAddress=", params.macAddress, "connId=", this.connId);
        return this.sendActionMessage(action, params, function (error, result) {
            if (error) {
                console.error("speakControl error=", error, "macAddress=", params.macAddress, "connId=", that.connId);
            }
            else {
                console.log("speakControl result=", result);
            }
            if (callback) {
                callback(error, result);
            }
        });
    }
    releaseDevice(sourceId, kind) {
        if (this.audioDevice === null) {
            return;
        }
        this.audioSenders.delete(sourceId);
        this.onSpeakableChange(sourceId, kind);
        if (this.audioSenders.size > 0) {
            return;
        }
        console.log("close audio device source=", sourceId);
        this.audioDevice.close();
        this.audioDevice = null;
    }
    bindAudioDevice(sourceId) {
        let midInfo = this.streamMapMediaInfo.get(sourceId);
        if (!midInfo) {
            console.error('Stream %s mid not found:', sourceId);
            this.onSpeakableChange(sourceId, MutedKind.NotFound);
            return;
        }
        let transceiver = null;
        if (midInfo.audioMid instanceof RTCRtpTransceiver) {
            transceiver = midInfo.audioMid;
        }
        else if (typeof midInfo.audioMid === "string") {
            // Find the transceiver by its mid
            transceiver = this.peerConn.getTransceivers().find(t => t.mid === midInfo.audioMid);
        }
        if (transceiver) {
            const piped = this.senderCreatedStreams.has(transceiver.sender);
            if (!piped) {
                const streams = transceiver.sender.createEncodedStreams();
                streams.readable.pipeTo(streams.writable);
                this.senderCreatedStreams.add(transceiver.sender);
            }
        }
        else {
            console.warn('Audio transceiver not found:', sourceId);
            this.onSpeakableChange(sourceId, MutedKind.NotFound);
            return;
        }
        if (!this.audioDevice) {
            this.audioDevice = new AudioDevice();
        }
        console.log("%s bind audio media device", sourceId);
        this.audioDevice.bind(transceiver.sender, (opened, err) => {
            if (opened) {
                this.audioSenders.add(sourceId);
                this.onSpeakableChange(sourceId);
            }
            else {
                this.releaseDevice(sourceId, MutedKind.Close);
            }
        });
    }
    onSpeakableChange(sourceId, mutedKind, err) {
        this.emitEvent(AudioWebRTC.EVENT_SPEAKABLE, { speakMuted: !!mutedKind, kind: mutedKind, error: err, sourceId: sourceId });
    }
    // two way audio end
    async checkCandidateType() {
        const stats = await this.peerConn.getStats(null);
        let type = CandidateType.None;
        stats.forEach((v) => {
            if (v.type === 'candidate-pair' && v.state === 'succeeded' && v.nominated) {
                if (type == CandidateType.Turn) {
                    return;
                }
                const remote = stats.get(v.remoteCandidateId);
                const local = stats.get(v.localCandidateId);
                type = CandidateType.P2P;
                if (remote?.candidateType === 'relay' || local?.candidateType === 'relay') {
                    type = CandidateType.Turn;
                    return;
                }
            }
        });
        if (this.candidateType !== type) {
            this.candidateType = type;
            this.emitEvent(GeneralWebRTC.EVENT_CANDIDATE_TYPE, type);
        }
    }
    // data webrtc start
    startExternalData() {
        if (this.dataRtcStarted) {
            console.warn('DataController started already: connId=' + this.connId);
            return false;
        }
        this.addEventListener(DataWebRTC.CHANNEL_DEVICE_SNAPSHOTS, (channel, channelObject) => {
            this.onDeviceSnapshot(channelObject);
        });
        GeneralWebRTC.EXTRA_LABELS.forEach((label) => {
            this.extralChannel.set(label, this.createExtraChannel(label));
        });
        this.dataRtcStarted = true;
        return true;
    }
    onExtraClose(channel, channelObject) {
        this.extralChannel.delete(channel.label);
        this.emitEvent(WebRTC.CHAN_CLOSE_PREFIX + channel.label, channel);
    }
    onExtraAction(channel, channelObject) {
        this.onActionMessage(channel, channelObject);
    }
    createExtraChannel(label) {
        if (this.extralChannel.has(label)) {
            return;
        }
        const channel = this.peerConn.createDataChannel(label);
        channel.onerror = (e) => {
            console.error("error in channel: connId=%s, error=%s, label=%s", this.connId, e, label);
        };
        const self = this;
        const dataChannel = new WebRTCChannel({
            connId: this.connId,
            channel: channel,
            onObject: this.onExtraAction.bind(this),
            onClose: this.onExtraClose.bind(this),
            onOpen: (channel) => {
                if (this.debugging)
                    console.debug("download channel opened: connId=%s, label=%s", this.connId, label);
                self.emitEvent(label + WebRTC.CHAN_OPEN_SUFFIX, channel);
                const index = this.unconnectedLabels?.findIndex(channel => label === channel);
                if (index >= 0) {
                    this.unconnectedLabels?.splice(index, 1);
                }
                if (this.unconnectedLabels?.length === 0) {
                    self.emitEvent(DataWebRTC.EXTRA_CHANNEL_CONNECTED);
                }
            },
        });
        return dataChannel;
    }
    doOnDeviceSnapshot(error, meta, obj) {
        const snapshot = {
            properties: meta.snapshotRes,
            imageBitmap: obj instanceof ImageBitmap ? obj : null,
            imageBlob: obj instanceof Blob ? obj : null,
        };
        const deviceSnapshot = {
            error,
            actId: meta.actId,
            camera: meta.snapshotRes.camera,
            snapshot,
        };
        // should or not?
        delete meta.snapshotRes.camera;
        this.emitEvent(DataWebRTC.EVENT_DEVICE_SNAPSHOTS, deviceSnapshot);
    }
    doOnCameraPresetSnapshot(error, meta, obj) {
        if (error) {
            console.error("presetGetImg: error=%s", error);
            meta?.callback?.(error, null);
            return;
        }
        const snapshot = {
            properties: meta.snapshotRes,
            imageBitmap: obj instanceof ImageBitmap ? obj : null,
        };
        const cameraPreset = {
            error,
            id: meta.id,
            actId: meta.actId,
            camera: meta?.snapshotRes?.camera,
            snapshot,
        };
        const actionMessage = new ActionMessage(meta.actId, PresetWebrtcAction.GET_IMG, cameraPreset);
        meta?.callback?.(null, actionMessage);
    }
    onDeviceSnapshot(channelObject) {
        const meta = channelObject.json;
        meta.tag = ImgTag.SNAPSHOT;
        const snapshotRes = meta.snapshotRes;
        if (snapshotRes.sent_at)
            snapshotRes.sent_duration = (new Date().getTime() - snapshotRes.sent_at) / 1000;
        if (this.debugging)
            console.debug("device_snapshot: meta=%s", JSON.stringify(meta));
        if (meta.error) {
            this.doOnDeviceSnapshot(AppError.fromObject(meta.error), meta, null);
            return;
        }
        const format = channelObject.json["snapshotRes"].format;
        if (format === "image/frame") {
            this.sendDecodeImage(meta, channelObject.binary);
        }
        else {
            const blob = new Blob([channelObject.binary], { type: "image/" + format });
            this.doOnDeviceSnapshot(null, meta, blob);
        }
    }
    sendDecodeImage(meta, data) {
        const worker = this.getWorker(WebRTC.LEGACY_WORKER_ID);
        const imageFrame = { data: arrayBufferLikeCopy(data), sequence: this.waiter.sequence };
        const args = { imageFrame, debugging: this.debugging };
        worker.postMessage({ type: WebRTCBase.ACTION_DECODE_IMAGE, args: args }, [imageFrame.data]);
        this.waiter.setAction({ meta });
    }
    onDecodeImage(args) {
        const action = this.waiter.unsetAction(args.sequence);
        if (action) {
            let error = null;
            if (args.error) {
                error = AppError.fromError(args.error);
            }
            if (action.meta.tag === ImgTag.SNAPSHOT) {
                this.doOnDeviceSnapshot(error, action.meta, args.imageBitmap);
            }
            else {
                this.doOnCameraPresetSnapshot(error, action.meta, args.imageBitmap);
            }
        }
    }
    getCandidateType() {
        return this.candidateType;
    }
    downloadVideo(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.Download, param, callback, timeout, this.extralChannel.get(label));
    }
    downloaderList(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.List, param, callback, timeout, this.extralChannel.get(label));
    }
    getMediaImg(param, callback, timeout, label = ExtraDataWebrtcLabel.Event) {
        return this.sendActionMessage(EventWebrtcAction.GetMedia, param, callback, timeout, this.extralChannel.get(label));
    }
    recordDaily(param, callback, timeout, label = ExtraDataWebrtcLabel.Download) {
        return this.sendActionMessage(DownloaderWebrtcAction.RecordDaily, param, callback, timeout, this.extralChannel.get(label));
    }
    abortVideo(param, callback) {
        return this.sendActionMessage(DownloaderWebrtcAction.Abort, param, callback);
    }
    completeVideo(param, callback) {
        return this.sendActionMessage(DownloaderWebrtcAction.Complete, param, callback);
    }
    presetList(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.LIST, param, callback, timeout);
    }
    presetSet(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.SET, param, callback, timeout);
    }
    presetDelete(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.DELETE, param, callback, timeout);
    }
    presetGoto(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        return this.sendActionMessage(PresetWebrtcAction.GOTO, param, callback, timeout);
    }
    presetGetImg(param, callback, timeout, label = ExtraDataWebrtcLabel.Preset) {
        console.log('presetGetImg: param=%s', JSON.stringify(param));
        const cbFun = (error, result) => {
            if (error) {
                console.error("presetGetImg: error=%s", error);
                callback?.(error, result);
                return;
            }
            const meta = {
                actId: result.id,
                error: result.error,
                id: result.data.id,
                snapshotRes: {
                    camera: result?.data?.camera,
                    width: 0,
                    height: 0,
                    format: 'string',
                    size: 0,
                    sent_at: 0,
                    sent_duration: 0,
                },
                callback: callback,
                tag: ImgTag.PRESET,
            };
            this.sendDecodeImage(meta, result.obj.binary);
        };
        return this.sendActionMessage(PresetWebrtcAction.GET_IMG, param, cbFun, timeout, this.extralChannel.get(label));
    }
    // data webrtc end
    //speaker webrtc start
    speakerPlay(param, callback) {
        return this.sendActionMessage(SpeakerWebrtcAction.PLAY, param, callback);
    }
    speakerAudioFiles(token, macAddress, callback) {
        const req = {
            token: token,
            macAddress: macAddress,
        };
        return this.sendActionMessage(SpeakerWebrtcAction.AUDIO_FILES, req, callback);
    }
    speakerConfigGet(token, macAddress, callback) {
        const req = {
            token: token,
            macAddress: macAddress,
            type: "get",
            object: MediaKind.AUDIO,
        };
        return this.sendActionMessage(SpeakerWebrtcAction.CONFIG, req, callback);
    }
    speakerConfigSet(token, macAddress, audioParams, callback) {
        if (audioParams) {
            if (!audioParams.jitterBuffer || (audioParams.jitterBuffer < 60 && audioParams.jitterBuffer > 2000)) {
                audioParams.jitterBuffer = 60;
            }
        }
        const req = {
            token: token,
            macAddress: macAddress,
            type: "set",
            object: MediaKind.AUDIO,
            audioParams: audioParams,
        };
        return this.sendActionMessage(SpeakerWebrtcAction.CONFIG, req, callback);
    }
    cameraSpeakerConfigGet(token, macAddress, remoteIndex, callback) {
        const req = {
            token: token,
            macAddress: macAddress,
            remoteIndex,
            type: "get",
            object: "output",
        };
        return this.sendActionMessage(CameraSpeakerAction.CONFIG, req, callback);
    }
    cameraSpeakerConfigSet(token, macAddress, remoteIndex, audioParams, callback) {
        const req = {
            token: token,
            macAddress: macAddress,
            remoteIndex,
            type: "set",
            object: "output",
            outputAudioParams: audioParams,
        };
        return this.sendActionMessage(CameraSpeakerAction.CONFIG, req, callback);
    }
    //speaker webrtc end
    pause(sourceId, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_PAUSE, { sourceId }, callback);
    }
    resume(sourceId, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_RESUME, { sourceId }, callback);
    }
    seek(seek, sourceId, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SEEK, { seek, sourceId }, callback);
    }
    scale(scale, sourceId) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SCALE, { scale, sourceId });
    }
    speed(speed, sourceId, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_SPEED, { speed, sourceId }, callback);
    }
    ptz(param) {
        const { cmd, token, camera, horizontal_speed = 5, vertical_speed = 5 } = param;
        return this.sendActionMessage(StreamWebRTC.ACTION_PTZ, { cmd, token, camera, horizontal_speed, vertical_speed });
    }
    ptzAreaZoomIn(param, callback) {
        return this.sendActionMessage(StreamWebRTC.ACTION_PTZ_AREA_ZOOM_IN, param, callback);
    }
}
GeneralWebRTC.MODE_CODE_TRACK = 1;
GeneralWebRTC.MODE_CODE_TUNNEL = 2;
GeneralWebRTC.TRACK_KIND_VIDEO = "video";
GeneralWebRTC.TRACK_KIND_AUDIO = "audio";
GeneralWebRTC.FILTER_AUDIO_CODEC_A = 'audio/PCMA';
GeneralWebRTC.FILTER_AUDIO_CODEC_U = 'audio/PCMU';
// action
GeneralWebRTC.ACTION_ADD_STREAM = 'singlePC.add';
GeneralWebRTC.ACTION_REMOVE_STREAM = 'singlePC.remove';
// events
GeneralWebRTC.EVENT_TIME_UPDATE = 'time_update';
GeneralWebRTC.EVENT_CANDIDATE_TYPE = 'candidate_type';
GeneralWebRTC.EXTRA_LABELS = Object.values(ExtraDataWebrtcLabel);
